import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';

import { getPositionList, getRankList } from '@entities/company/api/company';
import {
  createAdminEmployee,
  deleteEmployeeDelegate,
  getEmployeeByName,
  getEmployeeByTeam,
  getEmployeesByCompany,
  getEmployeeDelegate,
  putEmployeeDelegate,
  getMySign,
  saveMySign,
  getEmployeeInfo,
  getAdminEmployeeList,
  updateAdminEmployee,
} from '@entities/employee/api/employees';

/**
 * useDeleteEmployeeDelegateMutation
 * - 사용자의 대결자(대리자) 정보를 삭제하는 Mutation 훅
 * - JSX에서 전달한 onSuccess/onError는 기본 로직을 덮어쓰기 또는 추가 실행 가능
 */
function useDeleteEmployeeDelegateMutation(options = {}) {
  return useMutation({
    mutationFn: () => deleteEmployeeDelegate(),

    ...options,
  });
}

/**
 * useGetEmployeeByNameQuery
 * - 직원 이름으로 직원 정보를 검색하는 React Query 훅
 * - employeeName이 있을 때만 요청 실행(enabled 조건)
 * @param {string} employeeName - 검색할 직원명
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<object>}
 */
function useGetEmployeeByNameQuery(employeeName, options = {}) {
  return useQuery({
    queryKey: ['employee', 'search', employeeName],
    queryFn: () => getEmployeeByName(employeeName),
    enabled: !!employeeName,
    ...options,
  });
}

/**
 * useGetEmployeeDelegateQuery
 * - 현재 로그인 한 사람의 대결자를 조회하는 React Query 훅
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetEmployeeDelegateQuery(options = {}) {
  return useQuery({
    queryKey: ['employeeDelegate'],
    queryFn: () => getEmployeeDelegate(),
    // 대결자 정보는 자주 변경되지 않으므로 5분간 캐싱하여 API 호출 최소화
    // invalidate 시 즉시 refetch 됨 (staleTime과 별개로 동작)
    staleTime: 1000 * 60 * 5,
    refetchOnMount: true, // 마운트 시 재조회
    refetchOnWindowFocus: false, // 포커스 시 refetch 비활성
    ...options,
  });
}

/**
 * useGetEmployeesByCompanyQuery
 * - 회사코드로 회사 대표 직원 조회하는 React Query 훅
 * - companyCode 가 존재할때만 요청 실행(enabled 조건)
 * @param {number} companyCode = 회사코드
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetEmployeesByCompanyQuery(companyCode, options = {}) {
  return useQuery({
    queryKey: ['employeesByCompany', companyCode],
    queryFn: () => getEmployeesByCompany(companyCode),
    enabled: !!companyCode,
    ...options,
  });
}

/**
 * useGetEmployeesByTeamQuery
 * - 팀 ID로 직원 리스트를 조회하는 React Query 훅
 * - teamId가 존재할 때만 요청 실행(enabled 조건)
 * @param {number|string} teamId - 조회할 팀 ID
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetEmployeesByTeamQuery(teamId, options = {}) {
  return useQuery({
    queryKey: ['employees', teamId],
    queryFn: () => getEmployeeByTeam(teamId),
    enabled: !!teamId,
    ...options,
  });
}

/**
 * usePutEmployeeDelegateMutation
 * - 사용자의 대결자(대리자) 정보를 수정하는 Mutation 훅
 * - 내부 기본 onSuccess 로직 포함
 * - JSX에서 전달한 onSuccess/onError는 기본 로직을 덮어쓰기 또는 추가 실행 가능
 */
function usePutEmployeeDelegateMutation(options = {}) {
  return useMutation({
    mutationFn: (stead_enumber) => putEmployeeDelegate(stead_enumber),
    ...options,
  });
}

/**
 * useGetMySignQuery
 * - 사용자의 등록된 서명 조회 Query 훅
 */
function useGetMySignQuery(options = {}) {
  return useQuery({
    queryKey: ['mySign'],
    queryFn: () => getMySign(),
    // 서명 이미지는 자주 변경되지 않으므로 10분간 캐싱
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

/**
 * 사용자 서명 등록
 * - signFile(File) 을 받아 FormData로 업로드
 *
 */
function useSaveMySignMutation(options = {}) {
  return useMutation({
    mutationFn: async (signBlob) => {
      const formData = new FormData();
      formData.append('sign_img', signBlob);

      return await saveMySign(formData);
    },
    ...options,
  });
}
/**
 * 사원번호로 사원정보 조회 React Query 훅
 *
 * @param {number} employeeNumber - 조회할 사원번호
 * @param {Object} options - 추가 옵션 (enabled 등)
 */
function useEmployeeInfoQuery(employeeNumber, options = {}) {
  return useQuery({
    queryKey: ['employeeInfo', employeeNumber],
    queryFn: () => getEmployeeInfo(employeeNumber),
    enabled: !!employeeNumber, // employeeNumber 없으면 자동으로 호출 중단
    // 사원 정보는 자주 변경되지 않으므로 10분간 캐싱
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

/**
 * 여러 사원번호로 여러 사원 정보를 병렬 조회하는 React Query 훅
 *
 * @param {Array<{ enumber: number }>} members - enumber를 포함한 멤버 배열
 * @param {Object} options - React Query 공통 옵션 (선택)
 * @returns {Array<UseQueryResult>} - 각 사원번호별 Query 결과 배열
 */
function useEmployeeInfosQuery(members = [], options = {}) {
  return useQueries({
    queries: members.map((m) => ({
      queryKey: ['employeeInfo', m.enumber],
      queryFn: () => getEmployeeInfo(m.enumber),
      enabled: !!m.enumber,
      // 사원 정보는 자주 변경되지 않으므로 10분간 캐싱
      staleTime: 1000 * 60 * 10,
      ...options,
    })),
  });
}

/**
 * useGetAdminEmployeeListQuery
 * - 관리자 직원 리스트를 조회하는 React Query 훅
 * - companyCode가 존재할 때만 요청 실행(enabled 조건)
 * @param {Object} payload - RequestEmpDataDTO 형식
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetAdminEmployeeListQuery(payload, options = {}) {
  return useQuery({
    queryKey: ['adminEmployeeList', payload],
    queryFn: () => getAdminEmployeeList(payload),
    enabled: !!payload?.company,
    ...options,
  });
}

/**
 * 관리자 직원 추가 Mutation 훅
 * @param {object} options - React Query 옵션
 * @returns {UseMutationResult}
 */
function useCreateAdminEmployeeMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createAdminEmployee(payload),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['adminEmployeeList'] });
      if (options.onSuccess) {
        options.onSuccess(...args);
      }
    },
    ...options,
  });
}

/**
 * 관리자 직원 수정 Mutation 훅
 * @param {object} options - React Query 옵션
 * @returns {UseMutationResult}
 */
function useUpdateAdminEmployeeMutation(options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateAdminEmployee(payload),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: ['adminEmployeeList'] });
      if (options.onSuccess) {
        options.onSuccess(...args);
      }
    },
    ...options,
  });
}

/**
 * 직급 리스트 조회 React Query 훅
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetRankListQuery(options = {}) {
  return useQuery({
    queryKey: ['rankList'],
    queryFn: getRankList,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

/**
 * 직책 리스트 조회 React Query 훅
 * @param {object} options - React Query 옵션
 * @returns {UseQueryResult<Array>}
 */
function useGetPositionListQuery(options = {}) {
  return useQuery({
    queryKey: ['positionList'],
    queryFn: getPositionList,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
}

export {
  useDeleteEmployeeDelegateMutation,
  useGetEmployeeByNameQuery,
  useGetEmployeeDelegateQuery,
  useGetEmployeesByCompanyQuery,
  useGetEmployeesByTeamQuery,
  usePutEmployeeDelegateMutation,
  useGetMySignQuery,
  useSaveMySignMutation,
  useEmployeeInfoQuery,
  useEmployeeInfosQuery,
  useGetAdminEmployeeListQuery,
  useCreateAdminEmployeeMutation,
  useUpdateAdminEmployeeMutation,
  useGetRankListQuery,
  useGetPositionListQuery,
};
